import {
  ChangeDetectionStrategy,
  Component,
  TemplateRef,
  computed,
  DestroyRef,
  inject,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { DatePipe } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { Observable, finalize } from 'rxjs';
import { AvatarComponent } from '../../../../../../ui-components/lib/avatar/avatar.component';
import { BadgeComponent } from '../../../../../../ui-components/lib/badge/badge.component';
import {
  TableColumns,
  TableFilterTab,
} from '../../../../../../ui-components/lib/table/table.model';
import { DEFAULT_PAGE_SIZE } from '../../../../../common/constants/pagination.constants';
import {
  ApiResponse,
  Meta,
} from '../../../../../common/models/api-response.model';
import { RequestService } from '../../../../../common/services/request/request.service';
import {
  RequestSchedule,
  RequestScheduleType,
} from '../../../../../common/models/request-schedule.model';
import { FilteredTableComponent } from '../../../../../common/components/filtered-table/filtered-table.component';
import { FilteredTablePayload } from '../../../../../common/components/filtered-table/filtered-table.model';
import { DeleteConfirmationPopupComponent } from '../../../../../common/components/delete-confirmation-popup/delete-confirmation-popup.component';
import { ModalServiceService } from '../../../../../../ui-components/lib/modal/modal-service.service';
import { ToasterService } from '../../../../../../ui-components/services/toast/toast.service';
import {
  SCHEDULES_TAB_COLUMNS,
  SCHEDULES_TAB_FILTER_TABS,
} from './schedules-tab.constants';
import { StatusPipe } from '../../../../../../ui-components/pipes/status/status.pipe';
import { ActionList } from '../../../../../common/models/action-list.model';
import { ActionListComponent } from '../../../../../common/components/action-list/action-list.component';
import { EMPLOYEE_ROUTES } from '../../../../../common/constants/employee.routing.constants';
import { TenantService } from '../../../../../common/services/tenant/tenant.service';
import { AppPermissions } from '../../../../../common/constants/app-permissions';
import { PermissionsService } from '../../../../../common/services/permissions/permissions.service';
import { Permission } from '../../../../../common/models/permission.model';
import { Router } from '@angular/router';

@Component({
  selector: 'lib-schedules-tab',
  imports: [
    DatePipe,
    TranslocoPipe,
    BadgeComponent,
    AvatarComponent,
    FilteredTableComponent,
    StatusPipe,
    ActionListComponent,
    DeleteConfirmationPopupComponent,
  ],
  templateUrl: './schedules-tab.component.html',
  styleUrl: './schedules-tab.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class SchedulesTabComponent {
  private readonly requestService = inject(RequestService);
  private readonly transloco = inject(TranslocoService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly tenantService = inject(TenantService);
  private readonly permissionsService = inject(PermissionsService);
  private readonly modalService = inject(ModalServiceService);
  private readonly toaster = inject(ToasterService);
  private readonly router = inject(Router);

  cancelScheduleModal = viewChild<TemplateRef<unknown>>('cancelScheduleModal');

  readonly appPermissions = AppPermissions;
  readonly RequestScheduleType = RequestScheduleType;

  requestId = input.required<string | undefined>();
  userPermissions = input<Permission[]>([]);

  isLoading = signal<boolean>(false);
  isCancelling = signal<boolean>(false);
  dataSource = signal<RequestSchedule[]>([]);
  meta = signal<Meta | undefined>(undefined);

  columns = signal<TableColumns[]>(
    SCHEDULES_TAB_COLUMNS.map(column => ({
      ...column,
      header: this.transloco.translate(column.header),
    }))
  );

  tableFilterTabs = signal<TableFilterTab[]>(
    SCHEDULES_TAB_FILTER_TABS.map(tab => ({
      ...tab,
      label: this.transloco.translate(tab.label),
    }))
  );

  payload = signal<FilteredTablePayload>({
    page: 1,
    size: DEFAULT_PAGE_SIZE,
    sortColumn: { key: '', value: 'asc' },
    search: '',
    filters: {},
    tab: this.tableFilterTabs()[0],
  });

  selectedRow = signal<RequestSchedule | undefined>(undefined);
  actionsList = computed<ActionList[]>(() => {
    const isActiveSchedule = this.selectedRow()?.state.value === 'active';
    const scheduleId = this.selectedRow()?.id;
    const permissions = this.userPermissions();

    const actions: ActionList[] = [
      {
        key: 'view_details',
        hasIcon: true,
        label: this.transloco.translate('view_details_action_item'),
        isLink: true,
        routerLinks: [
          this.tenantService.tenantPath(
            `${EMPLOYEE_ROUTES.REQUESTS.DETAILS}/${this.requestId()}/${EMPLOYEE_ROUTES.REQUESTS.SCHEDULE}/${EMPLOYEE_ROUTES.REQUESTS.ACTION.VIEW}/${scheduleId}`
          ),
        ],
      },
    ];

    if (!isActiveSchedule) {
      return actions;
    }

    if (
      this.permissionsService.checkPermission(
        this.appPermissions.workflow.schedule.edit,
        permissions
      )
    ) {
      actions.push({
        key: 'edit',
        hasIcon: true,
        label: this.transloco.translate('edit_action_item'),
        hasDivider: true,
        isLink: true,
        routerLinks: [
          this.tenantService.tenantPath(
            `${EMPLOYEE_ROUTES.REQUESTS.DETAILS}/${this.requestId()}/${EMPLOYEE_ROUTES.REQUESTS.SCHEDULE}/${scheduleId}`
          ),
        ],
      });
    }

    if (
      this.permissionsService.checkPermission(
        this.appPermissions.workflow.schedule.cancel,
        permissions
      )
    ) {
      actions.push({
        key: 'deactivate',
        hasIcon: true,
        label: this.transloco.translate('cancel_schedule_action_item'),
        labelCustomStyle: 'text-destructive-500',
      });
    }

    return actions;
  });

  checkReturnedObservable = (): Observable<ApiResponse<RequestSchedule>> => {
    this.isLoading.set(true);
    const { page, size } = this.payload();

    return this.requestService
      .getRequestSchedules(this.requestId() as string, page, size)
      .pipe(finalize(() => this.isLoading.set(false)));
  };

  handleSchedulesSuccess(res: ApiResponse<RequestSchedule>) {
    this.dataSource.set(res.data);
    this.meta.set(res.meta);
  }

  handleApiCall() {
    this.checkReturnedObservable()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: res => this.handleSchedulesSuccess(res),
      });
  }

  onActionClick(actionKey: string) {
    if (actionKey === 'deactivate') {
      this.openCancelScheduleModal();
    }
  }

  openCancelScheduleModal() {
    const modal = {
      isOpen: true,
      hasCloseIcon: false,
      customizeContent: this.cancelScheduleModal(),
      width: '30',
    };

    this.modalService.modalContent.set(modal);
  }

  closeCancelScheduleModal() {
    this.modalService.modalContent.set({ isOpen: false });
  }

  confirmCancelSchedule() {
    const requestId = this.requestId();
    const scheduleId = this.selectedRow()?.id;

    if (!requestId || !scheduleId || this.isCancelling()) {
      return;
    }

    this.isCancelling.set(true);
    this.requestService
      .cancelRequestSchedule(requestId, String(scheduleId))
      .pipe(
        finalize(() => this.isCancelling.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: () => {
          this.closeCancelScheduleModal();
          this.toaster.add(
            this.transloco.translate('schedule_details_cancel_success_message'),
            '',
            '',
            'success'
          );
          this.handleApiCall();
        },
      });
  }

  navigateToScheduleDetails(schedule: RequestSchedule) {
    this.router.navigate([
      this.tenantService.tenantPath(
        `${EMPLOYEE_ROUTES.REQUESTS.DETAILS}/${this.requestId()}/${EMPLOYEE_ROUTES.REQUESTS.SCHEDULE}/${EMPLOYEE_ROUTES.REQUESTS.ACTION.VIEW}/${schedule.id}`
      ),
    ]);
  }
}
